/**
 * Hands an AI testing run's Overview to a summary editor in another tab.
 *
 * localStorage rather than router state: the editor opens in a NEW tab so the
 * AI testing page keeps its runs, and router state does not cross tabs. The
 * draft is read once and deleted, and anything left behind by a tab that never
 * opened is pruned after a day. Every access is guarded — storage can be
 * blocked, and then the button refuses rather than open the editor without
 * the draft (the saved text would pass for the run's). A draft names the
 * summary it was made for and is refused anywhere else.
 */
const PREFIX = "overviewDraft:";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const prune = () => {
  const now = Date.now();
  // Keys collected first: removing while walking by index can reorder them
  // and skip some.
  const keys = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  for (const key of keys) {
    try {
      const { at } = JSON.parse(localStorage.getItem(key)) || {};
      if (!at || now - at > MAX_AGE_MS) localStorage.removeItem(key);
    } catch {
      localStorage.removeItem(key);
    }
  }
};

/** Stores `{ summaryId, fields, model, bookTitle }`; returns the id to put in the URL, or null. */
export const saveOverviewDraft = (draft) => {
  try {
    prune();
    const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(PREFIX + id, JSON.stringify({ ...draft, at: Date.now() }));
    return id;
  } catch {
    return null;
  }
};

/**
 * Reads and deletes the draft. Null when it is missing, expired, unreadable,
 * or made for a different summary than `summaryId`.
 */
export const takeOverviewDraft = (id, summaryId) => {
  if (!id) return null;
  try {
    const raw = localStorage.getItem(PREFIX + id);
    localStorage.removeItem(PREFIX + id);
    const draft = raw ? JSON.parse(raw) : null;
    if (!draft?.fields || Date.now() - draft.at > MAX_AGE_MS) return null;
    if (String(draft.summaryId) !== String(summaryId)) return null;
    return draft;
  } catch {
    return null;
  }
};
